import React from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';

interface ImageComparisonProps {
  before: string;
  reference: string;
  after: string;
  height?: number;
  width?: number;
}

export const ImageComparison: React.FC<ImageComparisonProps> = ({
  before,
  reference,
  after,
  height = 400,
  width = 200,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const StepLabel = ({ number, text, width, height }: { number: number; text: string; width: number; height: number }) => (
    <Chip
      label={
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="start">
          <Box
            sx={{
              width: 24,
              height: 24,
              minWidth: 24,
              minHeight: 24,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '0.875rem'
            }}
          >
            {number}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              maxWidth: width,
              height: 'auto',
              whiteSpace: 'normal',
            }}
          >
            {text}
          </Typography>
        </Stack>
      }
      sx={{
        p: 1,
        height: 'auto',
        whiteSpace: 'normal',
        bgcolor: 'background.paper',
        boxShadow: 1,
        '& .MuiChip-label': {
          px: 1,
        },
      }}
    />
  );

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        bgcolor: 'background.paper',
        p: 2,
      }}
    >
      <Stack spacing={3}>
        {/* Top row with before and reference images */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          alignItems="center"
          justifyContent="center"
        >
          {/* Before Image */}
          <Stack spacing={1} alignItems="center">
            <StepLabel
              number={1}
              text="Find your ideal postcard art by browsing our library"
              width={width}
              height={height}
            />
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Box
                component="img"
                src={before}
                alt="Before"
                sx={{
                  width: width,
                  height: height,
                  objectFit: 'cover',
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              />
            </motion.div>
          </Stack>

          {/* Plus Icon */}
          <AddIcon
            sx={{
              fontSize: 64,
              color: 'primary.main',
            }}
            style={{ marginTop: isMobile ? 0 : '48px' }}
          />

          {/* Reference Image */}
          <Stack spacing={1} alignItems="center">
            <StepLabel
              number={2}
              text="Show us the content you want to add"
              width={width}
              height={height}
            />
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Box
                component="img"
                src={reference}
                alt="Reference"
                sx={{
                  width: width,
                  height: height,
                  objectFit: 'cover',
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              />
            </motion.div>
          </Stack>
        </Stack>

        {/* Bottom row with after image */}
        <Stack spacing={1} alignItems="center">
          <StepLabel
            number={3}
            text="Marvel at the results!"
            width={width}
            height={height}
          />
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Box
              component="img"
              src={after}
              alt="After"
              sx={{
                width: width,
                height: height,
                objectFit: 'cover',
                borderRadius: 2,
                boxShadow: 2,
                mx: 'auto',
              }}
            />
          </motion.div>
        </Stack>
      </Stack>
    </Paper>
  );
}; 